import React, { useMemo, useState } from 'react';
import { Button, Modal } from '@vacasa/react-components-lib';
import { Alert, TextField } from '@mui/material';
import { useBackfillPalUnitReviewSummariesMutation, useRegenerateUnitReviewSummariesMutation } from '../../services/ReviewsService';
import { parseAdminUnitIds } from '../../utils/shared.utils';
import './ReviewSummaries.scss';

export const ReviewSummaries: React.FC = () => {
    const [message, setMessage] = useState<string>('');
    const [isError, setIsError] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] = useState(false);
    const [adminUnitIdsInput, setAdminUnitIdsInput] = useState('');
    const [backfillPalUnitReviewSummaries, { isLoading }] = useBackfillPalUnitReviewSummariesMutation();
    const [regenerateUnitReviewSummaries, { isLoading: isRegenerating }] = useRegenerateUnitReviewSummariesMutation();

    const parsedAdminUnitIds = useMemo(() => parseAdminUnitIds(adminUnitIdsInput), [adminUnitIdsInput]);
    const isBusy = isLoading || isRegenerating;

    const onGenerate = async () => {
        try {
            setIsError(false);
            setMessage('');
            await backfillPalUnitReviewSummaries().unwrap();
            setMessage('Summary generation started. Messages were queued for PAL units without a summary.');
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to start summary generation. Please try again.');
        }
    };

    const onRegenerate = async () => {
        if (parsedAdminUnitIds.length === 0) {
            setIsError(true);
            setMessage('Enter at least one valid admin unit id.');
            return;
        }

        try {
            setIsError(false);
            setMessage('');
            const result = await regenerateUnitReviewSummaries({ admin_unit_ids: parsedAdminUnitIds }).unwrap();
            const notFoundSuffix = result.not_found?.length > 0 ? ` Not found: ${result.not_found.join(', ')}.` : '';
            setMessage(
                `Regeneration queued for ${result.queued} unit(s); deleted ${result.deleted} existing summar${
                    result.deleted === 1 ? 'y' : 'ies'
                }.${notFoundSuffix}`
            );
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to regenerate summaries. Please try again.');
        }
    };

    return (
        <div className="review-summaries">
            <div className="review-summaries-header">
                <h3>Review Summaries</h3>
                <p>Queue unit review summary generation for PAL units that do not already have a stored summary.</p>
            </div>

            {message && <Alert severity={isError ? 'error' : 'success'}>{message}</Alert>}

            <div className="review-summaries-actions">
                <Button variant="secondary" onClick={() => setIsConfirmOpen(true)} disabled={isBusy}>
                    Generate missing summaries
                </Button>
            </div>

            <div className="review-summaries-regenerate">
                <h4>Regenerate by unit</h4>
                <p>
                    Delete any existing summary for the given admin unit ids and enqueue forced regeneration. Separate ids with commas,
                    spaces, or new lines.
                </p>
                <TextField
                    label="Admin unit ids"
                    multiline
                    minRows={3}
                    fullWidth
                    value={adminUnitIdsInput}
                    onChange={(event) => setAdminUnitIdsInput(event.target.value)}
                    placeholder="1049281, 1000001"
                    disabled={isBusy}
                />
                <div className="review-summaries-actions">
                    <Button
                        variant="secondary"
                        onClick={() => setIsRegenerateConfirmOpen(true)}
                        disabled={isBusy || parsedAdminUnitIds.length === 0}
                    >
                        Regenerate summaries
                    </Button>
                </div>
            </div>

            <Modal showModal={isConfirmOpen} setShowModal={setIsConfirmOpen} size="medium">
                <div className="confirm-summaries-title">Confirm summary generation</div>
                <div className="confirm-summaries-body">
                    This will enqueue summary generation for PAL units missing a summary. Units that already have one are skipped.
                    Generation runs in the background and may take time.
                </div>
                <div className="confirm-summaries-actions">
                    <Button variant="info" onClick={() => setIsConfirmOpen(false)} disabled={isBusy}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            setIsConfirmOpen(false);
                            await onGenerate();
                        }}
                        disabled={isBusy}
                    >
                        Confirm generate
                    </Button>
                </div>
            </Modal>

            <Modal showModal={isRegenerateConfirmOpen} setShowModal={setIsRegenerateConfirmOpen} size="medium">
                <div className="confirm-summaries-title">Confirm summary regeneration</div>
                <div className="confirm-summaries-body">
                    This will delete existing summaries for {parsedAdminUnitIds.length} admin unit id
                    {parsedAdminUnitIds.length === 1 ? '' : 's'} and enqueue forced regeneration. Generation runs in the background and may
                    take time.
                </div>
                <div className="confirm-summaries-actions">
                    <Button variant="info" onClick={() => setIsRegenerateConfirmOpen(false)} disabled={isBusy}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            setIsRegenerateConfirmOpen(false);
                            await onRegenerate();
                        }}
                        disabled={isBusy}
                    >
                        Confirm regenerate
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
